import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { cn } from '../lib/cn';
import { formatNumber, formatPercent } from '../lib/format';

/**
 * Small CSS charts. All are single-series, so they use one colour
 * (--color-series-1) and no legend; the card title names what is plotted.
 * Marks follow the dataviz spec: bars <= 24px thick, 4px rounded data end,
 * square at the baseline, values as text tokens (never the series colour).
 */

export interface BarDatum {
  key: string;
  label: string;
  value: number;
}

/** Horizontal bars with the value at the tip. Every value is direct-labelled. */
export function BarList({ data, formatValue = formatNumber }: { data: BarDatum[]; formatValue?: (n: number) => string }) {
  const max = Math.max(0, ...data.map((d) => d.value));
  return (
    <ul className="space-y-1.5">
      {data.map((d) => {
        const pct = max > 0 ? (d.value / max) * 100 : 0;
        return (
          <li key={d.key} className="grid grid-cols-[minmax(0,11rem)_1fr] items-center gap-3 text-sm" title={`${d.label}: ${formatValue(d.value)}`}>
            <span className="truncate text-ink-2">{d.label}</span>
            <span className="flex items-center gap-2">
              <span className="grow-x h-2.5 rounded-full bg-series-1 hover:opacity-80" style={{ width: `${pct}%`, minWidth: d.value > 0 ? 2 : 0 }} />
              <span className="shrink-0 text-xs text-ink tabular-nums">{formatValue(d.value)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Vertical columns over time. Labels only the peak and the latest value;
 * every column has a hover/focus tooltip, and a table view is available.
 */
export function ColumnChart({
  data,
  height = 160,
  formatValue = formatNumber,
  tableLabels = { key: 'Period', value: 'Value' },
}: {
  data: BarDatum[];
  height?: number;
  formatValue?: (n: number) => string;
  tableLabels?: { key: string; value: string };
}) {
  const [active, setActive] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const max = Math.max(0, ...data.map((d) => d.value));
  const peak = data.findIndex((d) => d.value === max && max > 0);
  const labelEvery = Math.max(1, Math.ceil(data.length / 8));

  if (showTable) {
    return (
      <div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-2">
              <th className="py-1 font-medium">{tableLabels.key}</th>
              <th className="py-1 text-right font-medium">{tableLabels.value}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.key} className="border-t border-line">
                <td className="py-1 text-ink-2">{d.label}</td>
                <td className="py-1 text-right tabular-nums">{formatValue(d.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="mt-2 text-xs text-accent-ink hover:underline" onClick={() => setShowTable(false)}>
          Show chart
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative flex items-end gap-[2px] border-b border-baseline" style={{ height }}>
        <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-grid" />
        <span className="pointer-events-none absolute -top-4 left-0 text-[10px] text-muted tabular-nums">{formatValue(max)}</span>
        {data.map((d, i) => {
          const pct = max > 0 ? (d.value / max) * 100 : 0;
          const labelled = i === peak || i === data.length - 1;
          return (
            <div
              key={d.key}
              tabIndex={0}
              aria-label={`${d.label}: ${formatValue(d.value)}`}
              className="group relative flex h-full flex-1 items-end justify-center outline-none"
              onPointerEnter={() => setActive(i)}
              onPointerLeave={() => setActive(null)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
            >
              <div
                className={cn(
                  'grow-y w-full max-w-7 rounded-t-md transition-opacity',
                  i === data.length - 1 ? 'bg-brand-gradient' : 'bg-series-track',
                  active === i && 'opacity-75',
                )}
                style={{ height: `${pct}%`, minHeight: d.value > 0 ? 2 : 0 }}
              />
              {labelled && active !== i && d.value > 0 && (
                <span className="pointer-events-none absolute text-[10px] text-ink-2 tabular-nums" style={{ bottom: `calc(${pct}% + 2px)` }}>
                  {formatValue(d.value)}
                </span>
              )}
              {active === i && (
                <div
                  role="tooltip"
                  className="pointer-events-none absolute z-10 rounded-md border border-line bg-surface px-2 py-1 text-xs whitespace-nowrap shadow-md"
                  style={{ bottom: `calc(${pct}% + 6px)` }}
                >
                  <div className="font-semibold text-ink tabular-nums">{formatValue(d.value)}</div>
                  <div className="text-muted">{d.label}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex gap-[2px]">
        {data.map((d, i) => (
          <span key={d.key} className="flex-1 truncate text-center text-[10px] text-muted">
            {i % labelEvery === 0 ? d.label : ''}
          </span>
        ))}
      </div>
      <button type="button" className="mt-2 text-xs text-accent-ink hover:underline" onClick={() => setShowTable(true)}>
        Show as table
      </button>
    </div>
  );
}

/**
 * Meter for spend vs limit. Track is a light step of the same ramp; the fill
 * turns warning at `softPct` and critical at 100%. The caller always shows the
 * numbers as text next to it, so colour never carries meaning alone.
 */
export function Meter({ value, max, softPct = 80, className }: { value: number; max: number; softPct?: number; className?: string }) {
  const pct = max > 0 ? (value / max) * 100 : value > 0 ? 100 : 0;
  const fill = pct >= 100 ? 'bg-status-critical' : pct >= softPct ? 'bg-status-warning' : 'bg-series-1';
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-series-track/60', className)}
    >
      <div className={cn('grow-x h-full rounded-full', fill)} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

/** A KPI tile: label, value, optional sub-line. `lead` renders the gradient hero tile. */
export function StatTile({
  label,
  value,
  sub,
  lead,
  icon,
  style,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  lead?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn('rise lift rounded-card px-5 py-4 shadow-card', lead ? 'bg-brand-gradient text-on-accent' : 'border border-line bg-surface')}
    >
      <div className={cn('flex items-center justify-between text-xs', lead ? 'text-on-accent' : 'text-muted')}>
        {label}
        {icon}
      </div>
      <div className={cn('mt-1.5 text-2xl font-semibold tracking-tight', lead ? 'text-on-accent' : 'text-ink')}>{value}</div>
      {sub && <div className={cn('mt-0.5 text-xs', lead ? 'text-on-accent' : 'text-muted')}>{sub}</div>}
    </div>
  );
}

/** "38%" plus a thin two-part bar (me vs them). */
export function TalkRatio({ ratio }: { ratio: number | null }) {
  if (ratio == null) return <span className="text-muted">—</span>;
  return (
    <span className="flex items-center gap-2">
      <span className="w-9 text-right text-xs tabular-nums">{formatPercent(ratio)}</span>
      <span className="flex h-1.5 w-24 gap-[2px] overflow-hidden rounded-full" title={`Me ${formatPercent(ratio)} · Them ${formatPercent(1 - ratio)}`}>
        <span className="h-full bg-series-1" style={{ width: `${ratio * 100}%` }} />
        <span className="h-full flex-1 bg-series-track" />
      </span>
    </span>
  );
}
